/**
 * But why are we using regexes?
 *
 * Simple: javascript date libraries are designed to be liberal in what they accept, so they will accept almost anything.
 *
 * We want to be much more restrictive: we accept ISO-8601 or nothing.  (Ok, we will take MM-DD-YYYY for backwards compatiblity).
 *
 * So we first parse input with a regex to validate it matches the syntax we want, and only then hand it to a JS date library to parse.
 *
 * We do an additional step to validate the month value because dayjs will guess which position is the month if you give it a "month" > 12 and a "day" < 13.
 *
 * If you give it 2021-17-09, it helpfully decides that you meant September 17 2021.
 *
 * If customers are sending us YYYY-DD-MM we want to fail on everything that isn't valid (e.g. "months >12).
 * We want to blow up as much data as possible so the customer sees that they did it wrong.
 * */

import dayjs from "dayjs";
import dayjsTz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { DEFAULT_TIMEZONE } from "@kazoohr/types";

dayjs.extend(dayjsTz);
dayjs.extend(utc);
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

export type SupportedDateFormat = "YYYYMMDD" | "MMDDYYYY" | "MMDDYY" | "MMDD";
export type ParseType = "DATE_ONLY" | "FULL";

export type DeserializedDateTime = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  utcOffset?: number;
  timezone?: string;
  tzAbbreviation?: string;
  dateString?: string;
  date?: Date;
  tzSpecified: boolean;
};

const REGEX_YYYY_MM_DD =
  /^(?<year>\d{4})[/|-](?<month>\d{1,2})[/|-](?<day>\d{1,2})(=?[\s|T])?(?<hour>\d{1,2})?[:|\s]?(?<minute>\d{1,2})?[:|\s]?(?<second>\d{1,2})?(?<utcOffset>\s*[+|-]\d{1,2}:*\d{1,2})?\s?(?<timezone>[a-zA-Z]{4,}\/[a-zA-Z\s_]{3,})?(?<tzAbbreviation>[a-zA-Z]{3,4})?/;

const REGEX_MM_DD_YYYY =
  /^(?<month>\d{1,2})[/|-](?<day>\d{1,2})[/|-](?<year>\d{4})(=?[\s|T])?(?<hour>\d{1,2})?[:|\s]?(?<minute>\d{1,2})?[:|\s]?(?<second>\d{1,2})?(?<utcOffset>\s*[+|-]\d{1,2}:*\d{1,2})?\s?(?<timezone>[a-zA-Z]{3,}\/[a-zA-Z\s_]{3,})?(?<tzAbbreviation>[a-zA-Z]{3,4})?/;

// Obviously, this regex is problematic because there is no way to know what the user meant, so we assume US formats:
const REGEX_MM_DD_YY =
  /^(?<month>\d{1,2})[/|-](?<day>\d{1,2})[/|-](?<year>\d{1,2})(=?[\s|T])?(?<hour>\d{1,2})?[:|\s]?(?<minute>\d{1,2})?[:|\s]?(?<second>\d{1,2})?(?<utcOffset>\s*[+|-]\d{1,2}:*\d{1,2})?\s?(?<timezone>[a-zA-Z]{3,}\/[a-zA-Z\s_]{3,})?(?<tzAbbreviation>[a-zA-Z]{3,4})?/;

const REGEX_8601_NO_TIMEZONE =
  /^\d{4}-\d{2}-\d{2}[T|\s]\d{2}(:\d\d){0,3}(\.\d{1,8}){0,1}$/;
const REGEX_8601_OFFSET =
  /^\d{4}-\d{2}-\d{2}[T|\s]\d{2}(:\d\d){0,3}(\.\d{1,8}){0,1}[+|-]\d{1,2}:{0,1}\d{0,2}$/;
const REGEX_8601_ZULU =
  /^\d{4}-\d{2}-\d{2}[T|\s]\d{2}(:\d\d){0,3}(\.\d{1,8}){0,1}Z$/;
const REGEX_8601_TIMEZONE =
  /^\d{4}-\d{2}-\d{2}[T|\s]\d{2}(:\d\d){0,3}(\.\d{1,8}){0,1}\s?.*\/.*$/;

const ONLY_NUMBERS = /^\d+$/;

/**
 * Returns true if this is a legit ISO8601 date that includes Date and Time
 */
export function isValidIso8601Date(input: string) {
  return (
    isValidIso8601DateWithTimezone(input) || REGEX_8601_NO_TIMEZONE.test(input)
  );
}

/**
 * Returns true if this is a legit ISO8601 date that includes Date, Time, and Timezone
 */
export function isValidIso8601DateWithTimezone(input: string) {
  return (
    REGEX_8601_OFFSET.test(input) ||
    REGEX_8601_ZULU.test(input) ||
    REGEX_8601_TIMEZONE.test(input)
  );
}

/**
 * Uses a regex to extract the date parts, returning {month,day,year} as integers
 */
function parseMDY({ input, regex }: { input: string; regex: RegExp }):
  | {
      month: number;
      day: number;
      year: number;
      hour: number;
      minute: number;
      second: number;
      utcOffset?: number;
      timezone?: string;
      tzAbbreviation?: string;
    }
  | undefined {
  const defaultMatches = {
    groups: {
      year: undefined,
      month: undefined,
      day: undefined,
      hour: undefined,
      minute: undefined,
      second: undefined,
      utcOffset: undefined,
      timezone: undefined,
      tzAbbreviation: undefined,
    },
  };

  if (!input) {
    return parseRegexResult(defaultMatches.groups);
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- need to cast regex result
  const { groups } = (input.match(regex) as any) || defaultMatches;
  return parseRegexResult(groups);
}

function parseRegexResult({
  year,
  month,
  day,
  hour,
  minute,
  second,
  utcOffset,
  timezone,
  tzAbbreviation,
}: {
  year?: string;
  month?: string;
  day?: string;
  hour?: string;
  minute?: string;
  second?: string;
  utcOffset?: string;
  timezone?: string;
  tzAbbreviation?: string;
}): DeserializedDateTime {
  return {
    year: zeroForNan(year),
    month: zeroForNan(month),
    day: zeroForNan(day),
    hour: zeroForNan(hour),
    minute: zeroForNan(minute),
    second: zeroForNan(second),
    utcOffset: parseOffset(utcOffset),
    timezone,
    tzAbbreviation,
    tzSpecified: !!timezone || !!tzAbbreviation || !!utcOffset,
  };
}

function parseOffset(input?: string) {
  if (!input) {
    return undefined;
  }
  if (input.includes(":")) {
    const [hours, minutes] = input.split(":");
    return zeroForNan(hours) * 60 + zeroForNan(minutes);
  }
  return zeroForNan(input);
}

function zeroForNan(input?: string): number {
  if (!input || !input.trim()) {
    return 0;
  }
  const parsed = parseInt(input.trim());
  // istanbul ignore next -- this is a guard, but probably never gets hit since the regex only picks numbers
  if (isNaN(parsed)) {
    return 0;
  }
  return parsed;
}
/**
 * Converts a 2-digit year to a 4-digit year, assuming the year is in the current century if < 30.
 * @param year a probable year
 * @returns
 */
function windowYear(year: number) {
  if (year > 1900) {
    return year;
  }
  const MAX_YEAR_THIS_CENTURY = 2030 - 2000;

  if (year < 100 && year < MAX_YEAR_THIS_CENTURY) {
    return year + 2000;
  }
  return year + 1900;
}

/**
 * Pads and assembles an ISO-8601 compliant date part only
 *
 */
// eslint-disable-next-line complexity -- this is a complex function
function buildDate(
  input?: {
    month: number;
    year: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    timezone?: string;
    tzAbbreviation?: string;
    utcOffset?: number;
  },
  fullDate?: boolean
):
  | {
      month: number;
      year: number;
      day: number;
      hour: number;
      minute: number;
      second: number;
      dateString?: string;
      utcOffset?: number;
      tzAbbreviation?: string;
      timezone?: string;
      date?: Date;
      tzSpecified: boolean;
    }
  | undefined {
  /**
   * We cannot check for falsy-ness as that would return undefined for input such as 0.
   * If we return undefined here, the date will be un-parsable.
   * 0 as a number can represent 00 as the year 2000.
   * In theory, input?.year will never be undefined or null because prior to this in the call chain we will run
   * multiple regexes for numbers as well as parseInt if a number is not found.
   */
  // istanbul ignore next -- This is code that has been here long enough that I am afraid to remove it.

  if (input?.year === undefined || input?.month === null) {
    return;
  }
  if (input.month > 12) {
    return;
  }
  if (input.day < 1) {
    return;
  }

  const {
    year,
    month,
    day,
    hour,
    minute,
    second,
    utcOffset,
    timezone,
    tzAbbreviation,
  } = input;

  const timeZoneExpression = getTimezoneExpression(input);
  const timeBit = fullDate
    ? `T${input.hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}:${second.toString().padStart(2, "0")}`
    : "";

  const datebit = `${windowYear(year).toString().padStart(4, "0")}-${month
    .toString()
    .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;

  const dateString =
    utcOffset != undefined
      ? `${datebit}${timeBit}${getutcOffsetExpression(utcOffset)}`
      : `${datebit}${timeBit}`;

  const date = parseStringOrUndefined(dateString, timezone);

  return {
    year: windowYear(year),
    month,
    day,
    hour,
    minute,
    second,
    utcOffset,
    tzAbbreviation,
    timezone,
    date,
    dateString,
    tzSpecified: !!timeZoneExpression,
  };
}

function parseStringOrUndefined(dateString: string, timezone?: string) {
  const result = dayjs(dateString);
  // istanbul ignore else -- we should never get here, but this is a guard
  if (result.isValid()) {
    return timezone ? result?.tz(timezone, true).toDate() : result.toDate();
  }
}

function getutcOffsetExpression(utcOffset?: number) {
  if (utcOffset != undefined) {
    return `${utcOffset > 0 ? "+" : "-"}${Math.abs(utcOffset)
      .toString()
      .padStart(4, "0")}`;
  }
}

function getTimezoneExpression({
  utcOffset,
  timezone,
  tzAbbreviation,
}: {
  utcOffset?: number;
  timezone?: string;
  tzAbbreviation?: string;
}) {
  return getutcOffsetExpression(utcOffset) || timezone || tzAbbreviation || "";
}

/**
 * Accepts a raw date in the format YYYY/MM/DD (or YYYY-MM-DD or YYYY/M/D),
 * validates it,
 * and returns back a padded string in the format YYYY-MM-DD
 *
 */
function yyyymmddValidateAndTrim(input: string, parseType: ParseType) {
  // Handle non-delimiters
  let inputToParse = input;
  // If we know the length is 8, we know there are no delimiters
  if (input.length === 8 && input.match(ONLY_NUMBERS)) {
    inputToParse = `${input.slice(0, 4)}-${input.slice(4, 6)}-${input.slice(
      6,
      8
    )}`;
  }
  const mdy = parseMDY({
    input: inputToParse,
    regex: REGEX_YYYY_MM_DD,
  });
  return buildDate(mdy, parseType === "FULL");
}

/**
 * Accepts a raw date in the format YYYY/MM/DD (or YYYY-MM-DD or YYYY/M/D),
 * validates it,
 * and returns back a padded string in the format YYYY-MM-DD
 *
 */
function mmddyyyyValidateAndTrim(input: string, parseType: ParseType) {
  // Handle non-delimiters
  let inputToParse = input;
  if (input.length === 8 && input.match(ONLY_NUMBERS)) {
    inputToParse = `${input.slice(0, 2)}-${input.slice(2, 4)}-${input.slice(
      4,
      8
    )}`;
  }
  return buildDate(
    parseMDY({
      input: inputToParse,
      regex: REGEX_MM_DD_YYYY,
    }),
    parseType == "FULL"
  );
}

/**
 * Accepts a date in the format MM/DD/YY (or MM-DD-YY),
 * validates it,
 * and returns back a padded string in the format YYYY-MM-DD
 *
 * We don't want to support this, but here we are.
 */
function mmddyyValidateAndTrim(input: string, parseType: ParseType) {
  let inputToParse = input;
  // Handle non-delimiters
  if (input.length === 6 && input.match(ONLY_NUMBERS)) {
    inputToParse = `${input.slice(0, 2)}-${input.slice(2, 4)}-${input.slice(
      4,
      6
    )}`;
  }

  // If there's an input to parse and the year is 4 digits, return undefined
  // Kind of gross preliminary regex matcher, but easier than updating the happy path REGEX_MM_DD_YY to filter out YYYYY
  if (inputToParse && inputToParse.match(/\d{4}/)) {
    return;
  }

  return buildDate(
    parseMDY({
      input: inputToParse,
      regex: REGEX_MM_DD_YY,
    }),
    parseType == "FULL"
  );
}

/**
 * Returns the date formatted as ISO-8601 with no time or timezone if input is non-empty and a supported format.
 *
 * @deprecated You should not call this outside of DateWrapper.  Let DateWrapper handle use cases where we guess at format or timezone.
 */
export function getDateIfSupported(
  input: string,
  options: {
    parseType: ParseType;
    supportedDateFormats: SupportedDateFormat[];
  } = {
    parseType: "DATE_ONLY",
    supportedDateFormats: ["YYYYMMDD", "MMDDYYYY", "MMDDYY"],
  }
): DeserializedDateTime | undefined {
  const checkFunctions = [];
  if (options.supportedDateFormats.includes("YYYYMMDD")) {
    checkFunctions.push(yyyymmddValidateAndTrim);
  }
  if (options.supportedDateFormats.includes("MMDDYYYY")) {
    checkFunctions.push(mmddyyyyValidateAndTrim);
  }
  if (options.supportedDateFormats.includes("MMDDYY")) {
    checkFunctions.push(mmddyyValidateAndTrim); // yuck.
  }
  for (const check of checkFunctions) {
    const result = check(input, options.parseType);
    if (result?.dateString) {
      return result;
    }
  }
  return undefined;
}

/**
 * Returns just the date part of the date
 *
 * @deprecated This is probably not a good idea since you lose timezone information.
 */
export function getDatePart(input: Date) {
  return input.toISOString().split(/[T|t|\s]/)[0];
}

/**
 * Given a date string, returns the best guess as to the format
 */
export function determineDateFormat(
  input: string
): SupportedDateFormat | "UNKNOWN" {
  if (input.match(REGEX_8601_ZULU)) {
    return "YYYYMMDD";
  }
  if (input.match(REGEX_YYYY_MM_DD)) {
    return "YYYYMMDD";
  }
  if (input.match(REGEX_MM_DD_YYYY)) {
    return "MMDDYYYY";
  }
  if (input.match(REGEX_MM_DD_YY)) {
    return "MMDDYY";
  }
  return "UNKNOWN";
}
